/**
 * NAFos hero — staged entrance, subtle parallax and the "vine embrace".
 * Vanilla JS only; no dependencies. All motion honours reduced-motion.
 *
 * Vine embrace: ink branches with porcelain blossoms (fixed, hand-drawn
 * artwork in snippets/hero-vines.liquid) grow out from BEHIND the hero
 * image after the photograph settles — the image stays in the foreground.
 * JS only does placement (tucking each branch root under the image
 * edge) and deterministic delays; growth, blooming and swaying
 * are pure CSS transitions/animations.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ================= Vine embrace ================= */

  /**
   * Position each branch so its root corner (data-origin, a fraction of
   * its own bounding box) lands on its tuck point (data-tuck, viewBox
   * units) hidden under the image, scaled to data-target width and
   * rotated by data-rotate. Growth then scales from that same corner.
   */
  const layoutVines = (svg) => {
    for (const place of svg.querySelectorAll('.hv-place')) {
      const branch = place.querySelector('.hv-branch');
      if (!branch) continue;

      // Measure the branch itself: getBBox ignores the element's own
      // transform, so the CSS `scale: 0` starting state doesn't zero it.
      const box = branch.getBBox();
      const [originX, originY] = (place.dataset.origin || '50% 100%')
        .split(' ')
        .map((part) => parseFloat(part) / 100);
      const [tuckX, tuckY] = (place.dataset.tuck || '740 620').split(' ').map(Number);
      const rotate = parseFloat(place.dataset.rotate || 0);
      const scale = parseFloat(place.dataset.target || 400) / box.width;

      const anchorX = box.x + box.width * originX;
      const anchorY = box.y + box.height * originY;

      place.setAttribute(
        'transform',
        `translate(${tuckX},${tuckY}) rotate(${rotate}) scale(${scale.toFixed(4)}) translate(${(-anchorX).toFixed(1)},${(-anchorY).toFixed(1)})`
      );
      branch.style.transformOrigin = place.dataset.origin || '50% 100%';
    }
  };

  const setupVines = (hero) => {
    const layer = hero.querySelector('[data-vines]');
    const media = hero.querySelector('.hero-editorial__media');
    const svg = layer?.querySelector('svg');
    if (!layer || !media || !svg) return;

    layoutVines(svg);

    let active = false;

    const grow = () => {
      if (active) return;
      active = true;
      let index = 0;
      for (const branch of svg.querySelectorAll('.hv-branch')) {
        branch.style.setProperty('--hv-delay', `${(index * 0.11).toFixed(2)}s`);
        branch.style.setProperty('--hv-sway', `${(6.6 + index * 0.35).toFixed(2)}s`);
        index += 1;
      }
      layer.classList.add('is-live');
    };

    // A single, slow bloom after the photograph settles. Keeping this
    // non-interactive makes the signature moment consistent on touch and
    // desktop without asking the shopper to discover a hidden gesture.
    window.setTimeout(grow, reducedMotion.matches ? 0 : 560);
  };

  /* ================= Staged entrance + parallax ================= */

  const init = (hero) => {
    if (!hero || hero.dataset.heroInit) return;
    hero.dataset.heroInit = 'true';

    if (reducedMotion.matches) {
      hero.classList.add('is-staged');
    } else {
      const stageObserver = new IntersectionObserver(
        (entries, observer) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => hero.classList.add('is-staged'));
              });
              observer.disconnect();
            }
          }
        },
        { threshold: 0.15 }
      );
      stageObserver.observe(hero);
    }

    setupVines(hero);

    const layers = hero.querySelectorAll('[data-parallax-speed]');
    if (!layers.length || reducedMotion.matches || !hoverCapable.matches) return;

    let ticking = false;

    const applyParallax = () => {
      ticking = false;
      const rect = hero.getBoundingClientRect();
      const progress = -rect.top;
      if (progress < -rect.height || progress > rect.height) return;

      for (const layer of layers) {
        const speed = parseFloat(layer.dataset.parallaxSpeed) || 0;
        const shift = Math.max(-12, Math.min(12, progress * speed));
        layer.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(applyParallax);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    applyParallax();
  };

  const boot = () => {
    for (const hero of document.querySelectorAll('.hero-editorial')) init(hero);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
